<template>
  <div class="flex flex-col gap-4">
    <TextHeader>Energy Hub</TextHeader>
    <ToolbarComponent
      no-assets
      uncollapsed
      no-mode
      :allocation-points="filterStore.currentPortfolio?.energyHubAPs"
      :assets="energyHubAssets"
    />
    <EnergyHubChartPanel
      :metering-point="filterStore.currentMeteringPoint"
      :date-range="filterStore.dateRange"
      show-stats
    />
  </div>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from "vue"
import { startOfToday } from "date-fns"
import useFilterStore from "@/stores/filterStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import { ALLOCATION_POINT } from "@/stores/repower/meteringPointStoreFactories.js"
import { TextHeader } from "@repowerednl/ui-component-library"
import ToolbarComponent from "@/components/navigation/ToolbarComponent.vue"
import EnergyHubChartPanel from "@/components/panels/EnergyHubChartPanel.vue"
import useDefaultDateRange from "@/composables/useDefaultDateRange.js"

const filterStore = useFilterStore()
const meteringPointsStore = useMeteringPointsStore()

useDefaultDateRange([startOfToday(), startOfToday()])

/**
 * Construct the allocation points and assets to show in the metering point
 * picker. Only show allocation points that are part of an Energy Hub, and only
 * show assets that belong to those allocation points.
 *
 * This is necessary since energy hubs exist outside of the portfolio hierarchy.
 */
const energyHubAssets = computed(() => {
  const apIds = new Set(
    filterStore.currentPortfolio?.energyHubAPs.map((ap) => ap.id) ?? [],
  )
  return meteringPointsStore.assets.filter((asset) =>
    apIds.has(asset.allocationPointId),
  )
})

watch(
  () => filterStore.currentMeteringPoint,
  (currentMeteringPoint) => {
    if (currentMeteringPoint?.role !== ALLOCATION_POINT) {
      filterStore.clearMeteringPoint()
    }
  },
  { immediate: true },
)

/**
 * When the user navigates away, unselect the current metering point if it's not
 * in the current portfolio.
 */
onBeforeUnmount(() => {
  const inPortfolio =
    filterStore.currentMeteringPoint?.portfolioId ===
    filterStore.currentPortfolio?.id
  if (!inPortfolio) {
    filterStore.setMeteringPoint(null)
  }
})
</script>
